import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';

/**
 * 🔗 Unified Realtime Channel (consolidates 6 individual channels)
 * 
 * This hook manages a single WebSocket connection for all real-time updates:
 * - Game events (goals, cards, substitutions)
 * - Game status changes (live → finished)
 * - Standings updates
 * - Favorite games changes
 * 
 * Benefits:
 * - 83% reduction in WebSocket connections (6+ → 1)
 * - Reduced browser overhead
 * - Zero race conditions
 * - Centralized logging
 */
export function useRealtimeSync() {
  const queryClient = useQueryClient();

  useEffect(() => {
    console.log('🔗 Connecting to unified realtime channel (v2-accelerated)...');

    const channel = supabase
      .channel('app-realtime-v2')
      
      // 1️⃣ Game Events (goals, cards, substitutions)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'game_events'
      }, (payload) => {
        const latency = Date.now() - new Date(payload.commit_timestamp).getTime();
        const eventData = payload.new as any || payload.old as any;
        const fixtureId = eventData?.fixture_id;
        console.log(`⚡ postgres_changes: game_events | fixture: ${fixtureId} | latency: ${latency}ms`);
        queryClient.invalidateQueries({ queryKey: ['game-events', fixtureId] });
      })
      
      // 2️⃣ Games (status changes, new games → invalidate H2H on finish)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'games'
      }, (payload) => {
        const latency = Date.now() - new Date(payload.commit_timestamp).getTime();
        console.log(`⚡ postgres_changes: games | latency: ${latency}ms`);
        const game = payload.new as any;
        console.log(`⚡ Game update: ${game.fixture_id} (status: ${game.status})`);
        
        // Invalidate game lists
        queryClient.invalidateQueries({ queryKey: ['games'] });
        
        // Invalidate SG7 picks for live score updates and pick evaluations
        queryClient.invalidateQueries({ queryKey: ['sg7-picks'] });
        
        // If finished, invalidate H2H cache
        if (game.status === 'finished') {
          console.log('🏁 Game finished, invalidating H2H cache');
          queryClient.invalidateQueries({ 
            queryKey: ['head-to-head', game.home_team_id, game.away_team_id] 
          });
          queryClient.invalidateQueries({ 
            queryKey: ['head-to-head', game.away_team_id, game.home_team_id] 
          });
        }
      })
      
      // 3️⃣ Standings Cache (invalidates both regular and grouped standings)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'standings_cache'
      }, (payload) => {
        const latency = Date.now() - new Date(payload.commit_timestamp).getTime();
        const standingsData = payload.new as any;
        console.log(`⚡ postgres_changes: standings_cache | league: ${standingsData?.league_id} | latency: ${latency}ms`);
        
        // Invalidate all standings queries for instant UI refresh
        queryClient.invalidateQueries({ queryKey: ['standings-cache'] });
        queryClient.invalidateQueries({ queryKey: ['standings-cache-grouped'] });
        queryClient.invalidateQueries({ queryKey: ['all-leagues-standings'] });
      })
      
      // 4️⃣ Favorite Games
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'favorite_games'
      }, (payload) => {
        const latency = Date.now() - new Date(payload.commit_timestamp).getTime();
        const favoriteData = payload.new as any || payload.old as any;
        const userId = favoriteData?.user_id;
        console.log(`⚡ postgres_changes: favorite_games | user: ${userId} | latency: ${latency}ms`);
        queryClient.invalidateQueries({ queryKey: ['favorites'] });
      })
      
      // 5️⃣ Game Lineups
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'game_lineups'
      }, (payload) => {
        const latency = Date.now() - new Date(payload.commit_timestamp).getTime();
        const lineupData = payload.new as any || payload.old as any;
        const fixtureId = lineupData?.fixture_id;
        console.log(`⚡ postgres_changes: game_lineups | fixture: ${fixtureId} | latency: ${latency}ms`);
        queryClient.invalidateQueries({ queryKey: ['game-lineups', fixtureId] });
      })
      
      // 6️⃣ Player Statistics (Top Scorers) - Premium Realtime
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'player_statistics'
      }, (payload) => {
        const latency = Date.now() - new Date(payload.commit_timestamp).getTime();
        const statsData = payload.new as any || payload.old as any;
        const leagueApiId = statsData?.league_api_id;
        console.log(`⚡ postgres_changes: player_statistics | league: ${leagueApiId} | latency: ${latency}ms`);
        
        // Invalidar todas as queries de top scorers
        queryClient.invalidateQueries({ queryKey: ['top-scorers'] });
        queryClient.invalidateQueries({ queryKey: ['all-leagues-top-scorers'] });
      })
      
      // 7️⃣ Game Statistics (SG7 Radar - Zero Refetch Mode)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'game_statistics'
      }, (payload) => {
        const latency = Date.now() - new Date(payload.commit_timestamp).getTime();
        const newStats = payload.new as any;
        const fixtureId = newStats?.fixture_id;
        
        console.log(`⚡ postgres_changes: game_statistics INSERT | fixture: ${fixtureId} | latency: ${latency}ms`);
        
        if (fixtureId && newStats) {
          queryClient.setQueryData(['game-statistics', fixtureId], newStats);
        }
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'game_statistics'
      }, (payload) => {
        const latency = Date.now() - new Date(payload.commit_timestamp).getTime();
        const newStats = payload.new as any;
        const fixtureId = newStats?.fixture_id;
        
        console.log(`⚡ postgres_changes: game_statistics UPDATE | fixture: ${fixtureId} | latency: ${latency}ms | ZERO-REFETCH`);
        
        if (fixtureId && newStats) {
          queryClient.setQueryData(['game-statistics', fixtureId], newStats);
        }
      })
      
      .subscribe((status, err) => {
        console.log('🔗 Realtime status:', status);
        
        // Handle unexpected disconnections (e.g., Postgres restart during Supabase maintenance)
        if (status === 'CLOSED' || status === 'CHANNEL_ERROR') {
          console.warn('⚠️ Realtime disconnected unexpectedly:', err?.message || 'unknown reason');
          console.log('🔄 Supabase SDK will auto-reconnect with exponential backoff...');
        }
        
        if (status === 'SUBSCRIBED') {
          console.log('✅ Realtime channel connected successfully');
        }
      });

    return () => {
      console.log('🔌 Disconnecting unified realtime channel');
      supabase.removeChannel(channel);
    };
  }, [queryClient]);
}
